'use client'

import { useState, useEffect, createContext, useMemo, useRef, useCallback } from 'react'
import { useSession } from 'next-auth/react'
import { sendClientRequest } from '@/utils/client-request'
import { filterDiscounts, getProductDiscount, getBuyXGetYDiscount, discountAppliesToVariant, getRewardItemPrice, getEffectivePrice, isCustomerAuthActive, isLoyaltyActive, getCartTotal, getLoyaltyProductDiscount, isDiscountBetter, unitSaving, percentOff, regularSubtotalAfterSale, getVariantResolution, resolveUnitSavings } from '@/utils/util'
import { getMetaTrackingContext, trackAddPaymentInfo, trackAddToCart, trackInitiateCheckout } from '@/utils/tracking'
import { toast } from "@/lib/toast"
import { shopperHeaders } from '@/lib/commerce/services/shopper-headers'
import { validateCartApi } from '@/lib/commerce/services/cart-api'

export const CartContext = createContext<any>(null)

const getDismissedRewards = (): string[] => {
  try { return JSON.parse(sessionStorage.getItem('dismissedRewardDiscounts') || '[]') } catch (e) { return [] }
}

const dismissReward = (discountId: string) => {
  try { sessionStorage.setItem('dismissedRewardDiscounts', JSON.stringify([...new Set([...getDismissedRewards(), discountId])])) } catch (e) {}
}

// The automatic free-shipping discount: the active one itself, or the one running beside an order-type discount.
const automaticFreeShipping = (activeOrderDiscount: any) => {
  if (activeOrderDiscount?.type === 'freeShipping') return activeOrderDiscount
  return activeOrderDiscount?.companionFreeShipping || null
}

const RESOLUTION_DEBOUNCE_MS = 300

/*
  The cart figures from a server resolution (/discounts/automatic, or /discount/validate when a code is applied).
  Per regular line: discount savings = Σ (winner, stacked, order rewards) × regular units + Σ reward units, and the
  markdown is whatever else the line saved (price × quantity - lineTotal - discount savings). The applied code's
  own savings are left out (couponDiscount shows them once), and so are order-level parts, which are applied
  amounts not spent on lines (orderLevelAmount). Returns null when a cart line has no resolved line.
*/
const serverCartFigures = (resolution: any, products: any[]) => {
  const couponId = resolution.couponId || null
  const linesByKey = new Map<string, any>((resolution.lines || []).map((l: any) => [`${l.id}:${l.variantId ?? ''}`, l]))
  const lineSavings = new Map<string, number>()
  const addLineSaving = (id: string, amount: number) => { if (amount > 0) lineSavings.set(id, (lineSavings.get(id) || 0) + amount) }
  let salePriceSavings = 0
  for (const p of products) {
    const qty = p.quantity || 0
    if (p.isRewardItem || qty <= 0) continue
    const line = linesByKey.get(`${p.id}:${p.variant?.id ?? ''}`)
    if (!line) return null
    const price = p.variant?.price || 0
    const rewardUnits = line.rewardUnits || []
    const regularQty = Math.max(0, qty - rewardUnits.reduce((t: number, r: any) => t + (r.qty || 0), 0))
    let discounted = 0
    for (const entry of [line.winner, ...(line.stacked || []), ...(line.orderRewards || [])]) {
      if (!entry) continue
      const amount = (entry.unitSaving || 0) * regularQty
      addLineSaving(entry.id, amount)
      discounted += amount
    }
    for (const reward of rewardUnits) {
      const amount = (reward.unitSaving || 0) * (reward.qty || 0)
      addLineSaving(reward.id, amount)
      discounted += amount
    }
    const lineTotal = Number.isFinite(line.lineTotal) ? line.lineTotal : price * qty
    salePriceSavings += Math.max(0, price * qty - lineTotal - discounted)
  }
  const breakdown: { id: string, name: string, amount: number }[] = []
  let orderLevelAmount = 0
  for (const a of resolution.applied || []) {
    if (a.id === couponId) continue
    const onLines = lineSavings.get(a.id) || 0
    if (a.type === 'order') orderLevelAmount += Math.max(0, (a.amount || 0) - onLines)
    if (onLines > 0) breakdown.push({ id: a.id, name: a.name || 'Урамшуулал', amount: onLines })
  }
  return { salePriceSavings, breakdown, orderLevelAmount }
}

export function CartProvider({ vendor, initialLoyaltySummary = null, initialLoyaltyCategoryParents = {}, children }: { vendor: any, initialLoyaltySummary?: any, initialLoyaltyCategoryParents?: Record<string, string | null>, children: React.ReactNode }) {
  const { data: session } = useSession()

  const [cart, setCart] = useState<any>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const lastFetchedSessionRef = useRef<string | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [rewardLoading, setRewardLoading] = useState(false)
  // `auto`: opened by the cart itself (the gift auto-add), not by the shopper's Add button
  const [pendingReward, setPendingReward] = useState<{ item: any, type: 'buyXgetY' | 'order', auto?: boolean } | null>(null)

  const [appliedGiftCard, setAppliedGiftCard] = useState<any>(null)
  const [giftCardDiscount, setGiftCardDiscount] = useState(0)
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null)
  // The server's resolution of this cart: { key, couponId, applied, lines, freeShipping }
  const [serverResolution, setServerResolution] = useState<any>(null)
  const resolutionSeqRef = useRef(0)
  const resolvedKeyRef = useRef<string | null>(null)
  const [activeOrderDiscount, setActiveOrderDiscount] = useState<any>(null)
  const [appliedLoyaltyVoucher, setAppliedLoyaltyVoucher] = useState<any>(null)
  const [loyaltyPreview, setLoyaltyPreview] = useState<any>(null)
  const [loyaltySummary, setLoyaltySummary] = useState<any>(initialLoyaltySummary)
  const [loyaltyCategoryParents] = useState<Record<string, string | null>>(initialLoyaltyCategoryParents)
  const loyaltySeededRef = useRef(!!initialLoyaltySummary)
  const couponRestoredRef = useRef(false)
  const autoRewardKeysRef = useRef<Set<string>>(new Set())
  // Gifts whose variant picker the cart already opened by itself in this page load
  const autoPickerKeysRef = useRef<Set<string>>(new Set())

  const isGuest = useMemo(() => {
    return !session?.user?.id
  }, [session])
  const accessToken = session?.user?.accessToken

  const isLoggedIn = useMemo(() => !!session?.user?.id, [session])

  const allCartProducts = useMemo(() => cart?.products || [], [cart])

  const products = useMemo(() => {
    return allCartProducts.filter((p: any) => !p.isRewardItem)
  }, [allCartProducts])

  const rewardItems = useMemo(() => {
    return allCartProducts.filter((p: any) => p.isRewardItem && p.discountType === 'buyXgetY')
  }, [allCartProducts])

  const orderRewardItems = useMemo(() => {
    return allCartProducts.filter((p: any) => p.isRewardItem && p.discountType === 'order')
  }, [allCartProducts])

  const allProducts = useMemo(() => {
    return allCartProducts
  }, [allCartProducts])

  // What the preview endpoints read: regular lines, then reward lines at the price the shopper pays for them
  const previewProducts = useMemo(() => allCartProducts
    .filter((p: any) => (p.quantity || 0) > 0)
    .map((p: any) => p.isRewardItem
      ? { id: p.id, productId: p.id, variantId: p.variant?.id, isRewardItem: true, price: getRewardItemPrice(p), quantity: p.quantity, discountId: p.discountId || null }
      : {
        id: p.id,
        productId: p.id,
        variantId: p.variant?.id,
        categoryId: (p.product || p)?.categoryId || (p.product || p)?.categories?.[0] || null,
        price: p.variant?.price || 0,
        priceSale: p.variant?.priceSale || null,
        quantity: p.quantity,
      }), [allCartProducts])

  const couponCode: string | null = (appliedCoupon?.discount || appliedCoupon)?.code || null
  const resolutionKeyFor = useCallback((code: string | null) => JSON.stringify([code, previewProducts]), [previewProducts])
  const resolutionKey = resolutionKeyFor(couponCode)
  // Server numbers count only for the cart they were resolved for; until a fresh one arrives the local mirror shows
  const freshResolution = serverResolution && serverResolution.key === resolutionKey ? serverResolution : null

  // A /discount/validate answer: the coupon, and the cart resolved with it when the answer carries one
  const storeCouponResult = (data: any, key: string) => {
    setAppliedCoupon({ ...data, forKey: key })
    if (Array.isArray(data?.applied) && Array.isArray(data?.lines)) {
      // Any resolution still in flight was asked for without this answer
      resolutionSeqRef.current++
      resolvedKeyRef.current = key
      setServerResolution({ key, couponId: data.discount?.id || null, applied: data.applied, lines: data.lines, freeShipping: data.applied.some((a: any) => a.freeShipping) })
    }
  }

  useEffect(() => {
    if (session) {
      const currentSessionId = isGuest ? (session as any).sessionId : (session.user?.id || (session as any).sessionId)
      if (currentSessionId) setSessionId(currentSessionId)
    }
  }, [session, isGuest])

  useEffect(() => {
    const currentId = session?.user?.id || sessionId
    if (vendor && currentId && currentId !== lastFetchedSessionRef.current) {
      lastFetchedSessionRef.current = currentId
      setSessionId(currentId)
      fetchCart({ newSessionId: currentId })
    }
  }, [session, vendor, sessionId])

  useEffect(() => {
    if (vendor?.id || vendor) {
      fetchActiveOrderDiscount()
    }
  }, [vendor])

  // ?code=XXXX on any storefront URL: kept like a typed code, then applied by the restore below once the cart loads
  useEffect(() => {
    try {
      const urlCode = new URLSearchParams(window.location.search).get('code')
      if (urlCode && urlCode.trim()) sessionStorage.setItem('appliedCouponCode', urlCode.trim().toUpperCase())
    } catch (e) {}
  }, [])

  useEffect(() => {
    if (couponRestoredRef.current || !cart?.products?.length || !vendor || !sessionId) return
    couponRestoredRef.current = true
    try {
      const savedCode = sessionStorage.getItem('appliedCouponCode')
      if (!savedCode) return
      const vendorId = vendor?.id || vendor
      const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
      sendClientRequest({
        url: '/discount/validate',
        method: 'POST',
        auth: false,
        headers,
        body: { code: savedCode, products: previewProducts },
      }).then((res: any) => {
        const { data, error } = res || {}
        if (!error && data) {
          storeCouponResult(data, resolutionKeyFor(data.discount?.code || savedCode))
        } else {
          sessionStorage.removeItem('appliedCouponCode')
        }
      })
    } catch (e) {
      sessionStorage.removeItem('appliedCouponCode')
    }
  }, [cart, vendor, sessionId])

  /*
    The server resolves every cart change (and every coupon change) as checkout will: /discounts/automatic, or
    /discount/validate with the applied code so the code competes with the automatic discounts. Debounced, and a
    later request makes an earlier answer stale (sequence number). A failed call stores nothing, so the local
    mirror keeps showing.
  */
  useEffect(() => {
    if (!vendor || !previewProducts.some((p: any) => !p.isRewardItem)) return
    if (resolvedKeyRef.current === resolutionKey) return
    const seq = ++resolutionSeqRef.current
    const key = resolutionKey
    const code = couponCode
    const timer = setTimeout(async () => {
      const vendorId = vendor?.id || vendor
      const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
      // sendClientRequest can resolve to false or undefined (network error, 502 logout path)
      const result: any = (await sendClientRequest({
        url: code ? '/discount/validate' : '/discounts/automatic',
        method: 'POST',
        auth: false,
        headers,
        body: code ? { code, products: previewProducts } : { products: previewProducts },
      })) || {}
      const { data, error } = result
      if (seq !== resolutionSeqRef.current) return
      if (error || !data || !Array.isArray(data.applied) || !Array.isArray(data.lines)) return
      resolvedKeyRef.current = key
      if (code) {
        setAppliedCoupon((prev: any) => ((prev?.discount || prev)?.code === code ? { ...data, forKey: key } : prev))
        setServerResolution({ key, couponId: data.discount?.id || null, applied: data.applied, lines: data.lines, freeShipping: data.applied.some((a: any) => a.freeShipping) })
      } else {
        setServerResolution({ key, couponId: null, applied: data.applied, lines: data.lines, freeShipping: data.freeShipping === true })
      }
    }, RESOLUTION_DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolutionKey, vendor, sessionId, isGuest, accessToken])

  const fetchActiveOrderDiscount = async () => {
    try {
      const vendorId = vendor?.id || vendor
      const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
      const { data, error } = await sendClientRequest({ url: '/discount/order/public', auth: false, headers })
      if (!error && data) {
        setActiveOrderDiscount(data || null)
      }
    } catch (error) {
      console.error('Error fetching order discount:', error)
    }
  }

  const amount = useMemo(() => {
    return allProducts.reduce((total: number, p: any) => {
      return total + (p.variant?.price || 0) * (p.quantity || 0)
    }, 0)
  }, [allProducts])

  const serverFigures = useMemo(() => (freshResolution ? serverCartFigures(freshResolution, allProducts) : null), [freshResolution, allProducts])

  const { sale, salePriceSavings, discountSavings, discountBreakdown } = useMemo(() => {
    let salePriceSavings = 0
    let discountSavings = 0
    const breakdownMap: Record<string, { id: string, name: string, amount: number }> = {}
    const addToBreakdown = (id: string, name: string, amount: number) => {
      if (!breakdownMap[id]) breakdownMap[id] = { id, name, amount: 0 }
      breakdownMap[id].amount += amount
    }
    // Server resolution: regular lines from it; reward lines (their own cart lines) are priced here as checkout does
    if (serverFigures) {
      salePriceSavings = serverFigures.salePriceSavings
      for (const row of serverFigures.breakdown) {
        discountSavings += row.amount
        addToBreakdown(row.id, row.name, row.amount)
      }
    }
    for (const product of allProducts) {
      const variant = product.variant
      if (!variant?.price) continue
      const qty = product.quantity || 0

      if (product.isRewardItem) {
        const rewardPrice = getRewardItemPrice(product)
        const saving = (variant.price - rewardPrice) * qty
        discountSavings += saving
        if (product.discountId && saving > 0) addToBreakdown(product.discountId, product.discountName || 'Урамшуулал', saving)
        continue
      }
      if (serverFigures) continue

      const productData = product.product || product
      const rawDiscounts = product.discounts || productData?.discounts
      const discounts = filterDiscounts(rawDiscounts, isLoggedIn, variant?.id)

      // The server's per-variant resolution in the product payload: markdown, winner and stacked entries per unit
      const resolvedUnit = getVariantResolution(discounts, variant.id, isLoggedIn) ? resolveUnitSavings(variant.price, variant.priceSale, discounts, variant.id, isLoggedIn) : null
      if (resolvedUnit) {
        salePriceSavings += resolvedUnit.markdown * qty
        for (const entry of resolvedUnit.entries) {
          const saving = entry.unitSaving * qty
          discountSavings += saving
          if (saving > 0) addToBreakdown(entry.id, entry.name, saving)
        }
        continue
      }

      const productDiscount = getProductDiscount(discounts)

      if (productDiscount && productDiscount.method !== 'code' && discountAppliesToVariant(productDiscount, variant)) {
        const basePrice = productDiscount.discountBase === 'sale' && variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price
          ? variant.priceSale : variant.price
        const discountAmount = unitSaving(basePrice, productDiscount)

        if (productDiscount.discountBase === 'sale' && variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price) {
          salePriceSavings += (variant.price - variant.priceSale) * qty
          const saving = discountAmount * qty
          discountSavings += saving
          if (saving > 0) addToBreakdown(productDiscount.id, productDiscount.name || 'Урамшуулал', saving)
        } else {
          const pSaleSavings = variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price
            ? variant.price - variant.priceSale : 0
          if (discountAmount >= pSaleSavings) {
            const saving = discountAmount * qty
            discountSavings += saving
            if (saving > 0) addToBreakdown(productDiscount.id, productDiscount.name || 'Урамшуулал', saving)
          } else {
            salePriceSavings += pSaleSavings * qty
          }
        }
        continue
      }

      const hasCouponOriginalBase = appliedCoupon?.discount?.type === 'productDiscount' && appliedCoupon?.discount?.discountBase === 'original'
      let couponTargetsThisProduct = false
      if (hasCouponOriginalBase) {
        const codeDiscount = rawDiscounts?.find((d: any) =>
          d.type === 'productDiscount' && d.method === 'code' && d.id === appliedCoupon.discount.id
        )
        if (codeDiscount) {
          if (!codeDiscount.variantIds?.length || codeDiscount.variantIds.includes(variant.id)) {
            couponTargetsThisProduct = true
          }
        }
      }

      if (!couponTargetsThisProduct && variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price) {
        salePriceSavings += (variant.price - variant.priceSale) * qty
      }
    }
    return { sale: salePriceSavings + discountSavings, salePriceSavings, discountSavings, discountBreakdown: Object.values(breakdownMap).filter(b => b.amount > 0) }
  }, [allProducts, isLoggedIn, appliedCoupon, serverFigures])

  // Local mirror: what the markdowns and automatic product discounts take off the regular lines
  const localRegularSale = (list: any[]) => list.reduce((t: number, p: any) => {
    if (p.isRewardItem) return t
    const v = p.variant
    if (!v?.price) return t
    const ds = filterDiscounts(p.discounts || (p.product || p)?.discounts, isLoggedIn, v?.id)
    return t + (v.price - (getEffectivePrice(v, ds, isLoggedIn) ?? v.price)) * (p.quantity || 0)
  }, 0)

  // Local mirror of the automatic order discount's order-level amount (used only without a server resolution)
  const localAutomaticOrderAmount = useMemo(() => {
    if (!activeOrderDiscount || activeOrderDiscount.type !== 'order') return 0
    const minOrderAmount = activeOrderDiscount.minOrderAmount || 0

    const regularAmount = products.reduce((t: number, p: any) => {
      if (p.isRewardItem) return t
      return t + (p.variant?.price || 0) * (p.quantity || 0)
    }, 0)

    const regularSale = localRegularSale(products)

    const subtotal = regularAmount - regularSale
    if (regularSubtotalAfterSale(products) < minOrderAmount) return 0

    const orderLevelTarget = activeOrderDiscount.targets?.find(
      (t: any) => t.role === 'target' && t.targetId === null
    )
    if (!orderLevelTarget) return 0

    if (orderLevelTarget.valueType === 'percent' && orderLevelTarget.valuePercent) {
      return percentOff(subtotal, orderLevelTarget.valuePercent)
    } else if (orderLevelTarget.valueType === 'amount' && orderLevelTarget.valueAmount) {
      return Math.min(subtotal, orderLevelTarget.valueAmount)
    }
    return 0
  }, [products, activeOrderDiscount, isLoggedIn])

  // Local mirror of an order-type code's own amount, before it competes with the automatic order discount
  const localCouponOrderAmount = useMemo(() => {
    const discount = appliedCoupon?.discount || appliedCoupon
    if (discount?.type !== 'order') return 0
    const regularAmount = products.reduce((t: number, p: any) => {
      if (p.isRewardItem) return t
      return t + (p.variant?.price || 0) * (p.quantity || 0)
    }, 0)
    const regularSale = localRegularSale(products)

    const subtotal = regularAmount - regularSale
    if (regularSubtotalAfterSale(products) < (discount.minOrderAmount || 0)) return 0

    if (discount.valueType === 'percent' && discount.valuePercent) {
      return percentOff(subtotal, discount.valuePercent)
    } else if (discount.valueType === 'amount' && discount.valueAmount) {
      return Math.min(subtotal, discount.valueAmount)
    }
    return 0
  }, [appliedCoupon, products, isLoggedIn])

  // Locally a non-stackable order code and the automatic order discount compete: only the larger applies (a tie to the code)
  const localOrderCouponWins = localCouponOrderAmount > 0 && localCouponOrderAmount >= localAutomaticOrderAmount

  const couponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0
    // An automatic discount or the markdown beat the code: it saves nothing
    if (appliedCoupon.beatenBy) return 0
    // The server's number for this cart (the code's own applied amount)
    if (appliedCoupon.forKey === resolutionKey && Array.isArray(appliedCoupon.applied) && Array.isArray(appliedCoupon.lines)) {
      return Number(appliedCoupon.discountAmount) || 0
    }
    const discount = appliedCoupon.discount || appliedCoupon

    if (discount.type === 'productDiscount') {
      return products.reduce((total: number, product: any) => {
        if (product.isRewardItem) return total
        const productData = product.product || product
        const variant = product.variant
        const rawDiscounts = product.discounts || productData?.discounts
        const codeDiscount = rawDiscounts?.find((d: any) =>
          d.type === 'productDiscount' && d.method === 'code' && d.id === discount.id
        )
        if (!codeDiscount) return total
        if (codeDiscount.variantIds?.length > 0 && !codeDiscount.variantIds.includes(variant?.id)) return total

        const basePrice = codeDiscount.discountBase === 'original'
          ? variant.price
          : (variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price ? variant.priceSale : variant.price)

        const discountAmount = unitSaving(basePrice, codeDiscount)

        return total + (discountAmount * (product.quantity || 0))
      }, 0)
    }

    if (discount.type === 'order') return localOrderCouponWins ? localCouponOrderAmount : 0

    return 0
  }, [appliedCoupon, products, isLoggedIn, resolutionKey, localOrderCouponWins, localCouponOrderAmount])

  const orderDiscountAmount = useMemo(() => {
    // Server resolution: the order-level parts of every applied order discount except the code (couponDiscount)
    if (serverFigures) return serverFigures.orderLevelAmount
    // Local mirror: an order-type code that saves at least as much takes the order slot instead
    if (localOrderCouponWins) return 0
    return localAutomaticOrderAmount
  }, [serverFigures, localOrderCouponWins, localAutomaticOrderAmount])

  const buyXGetYInfo = useMemo(() => {
    const computeInfo = (discount: any, isFromCoupon: boolean) => {
      const buyXMinQuantity = discount.buyXMinQuantity || 1
      const getYMaxQuantity = discount.getYMaxQuantity || 1
      const applyMode = discount.applyMode || 'once'
      const buyXMatchMode = discount.buyXMatchMode || 'any'
      const getYMatchMode = discount.getYMatchMode || 'any'
      const discountId = discount.id

      let triggerCount = 0
      let qualifies = false
      let minPerProduct = 0

      if (buyXMatchMode === 'all') {
        const triggerTargets = discount.triggerTargets || []
        const triggerProductQtyMap = new Map<string, number>()
        for (const product of products) {
          if (product.isRewardItem) continue
          if (isFromCoupon) {
            const existing = triggerProductQtyMap.get(product.id) || 0
            triggerProductQtyMap.set(product.id, existing + (product.quantity || 0))
          } else {
            const rawDiscounts = product.discounts || (product.product || product)?.discounts
            const bxgy = getBuyXGetYDiscount(rawDiscounts)
            if (bxgy?.id === discountId && bxgy.role === 'trigger') {
              const tt = (bxgy.triggerTargets || []).find((t: any) => t.targetType === 'product' && t.targetId === product.id)
              if (tt?.variantIds?.length > 0 && !tt.variantIds.includes(product.variant?.id)) continue
              const existing = triggerProductQtyMap.get(product.id) || 0
              triggerProductQtyMap.set(product.id, existing + (product.quantity || 0))
            }
          }
        }
        qualifies = triggerTargets.every((tp: any) => (triggerProductQtyMap.get(tp.targetId) || 0) >= buyXMinQuantity)
        minPerProduct = triggerProductQtyMap.size > 0 ? Math.min(...Array.from(triggerProductQtyMap.values())) : 0
        triggerCount = Array.from(triggerProductQtyMap.values()).reduce((sum, qty) => sum + qty, 0)
      } else {
        for (const product of products) {
          if (product.isRewardItem) continue
          if (isFromCoupon) {
            triggerCount += product.quantity || 0
          } else {
            const rawDiscounts = product.discounts || (product.product || product)?.discounts
            const bxgy = getBuyXGetYDiscount(rawDiscounts)
            if (bxgy?.id === discountId && bxgy.role === 'trigger') {
              const tt = (bxgy.triggerTargets || []).find((t: any) => t.targetType === 'product' && t.targetId === product.id)
              if (tt?.variantIds?.length > 0 && !tt.variantIds.includes(product.variant?.id)) continue
              triggerCount += product.quantity || 0
            }
          }
        }
        qualifies = triggerCount >= buyXMinQuantity
      }

      let maxRewardCount = 0
      if (qualifies) {
        if (applyMode === 'once') {
          maxRewardCount = getYMaxQuantity
        } else {
          const cycleBase = buyXMatchMode === 'all' ? minPerProduct : triggerCount
          const cycles = Math.floor(cycleBase / buyXMinQuantity)
          maxRewardCount = cycles * getYMaxQuantity
        }
      }

      return {
        discount,
        qualifies,
        triggerCount,
        maxRewardCount,
        getYMatchMode,
        getYProductLimit: getYMatchMode === 'custom' ? (discount.getYProductLimit || 1) : getYMatchMode === 'all' ? Infinity : 1,
        rewardProducts: discount.rewardProducts || [],
        isFromCoupon,
      }
    }

    const results: any[] = []

    if (appliedCoupon?.discount?.type === 'buyXgetY' || appliedCoupon?.type === 'buyXgetY') {
      const discount = appliedCoupon.discount || appliedCoupon
      results.push(computeInfo(discount, true))
    }

    const seenIds = new Set<string>(results.map((r: any) => r.discount.id))
    for (const product of products) {
      if (product.isRewardItem) continue
      const rawDiscounts = product.discounts || (product.product || product)?.discounts
      const discounts = filterDiscounts(rawDiscounts, isLoggedIn, product.variant?.id)
      const bxgy = getBuyXGetYDiscount(discounts)
      if (bxgy && bxgy.role === 'trigger' && bxgy.method !== 'code' && !seenIds.has(bxgy.id)) {
        const triggerTargets = bxgy.triggerTargets || []
        const matchingTarget = triggerTargets.find((t: any) => t.targetType === 'product' && t.targetId === product.id)
        if (matchingTarget?.variantIds?.length > 0 && !matchingTarget.variantIds.includes(product.variant?.id)) continue
        seenIds.add(bxgy.id)
        results.push(computeInfo(bxgy, false))
      }
    }

    return results.length > 0 ? results : null
  }, [products, appliedCoupon, isLoggedIn])

  const availableOrderRewardItems = useMemo(() => {
    if (!activeOrderDiscount) return []
    if (appliedCoupon) return []
    const minOrderAmount = activeOrderDiscount.minOrderAmount || 0
    if (regularSubtotalAfterSale(products) < minOrderAmount) return []

    const rewardTargets = (activeOrderDiscount.targets || []).filter((t: any) => t.role === 'reward')
    return rewardTargets.filter((t: any) => {
      const alreadyAdded = orderRewardItems.some((r: any) => r.id === t.product?.id)
      return !alreadyAdded && t.product
    }).map((t: any) => ({
      ...t.product,
      discountBase: activeOrderDiscount.discountBase || 'original',
      valueType: t.valueType,
      valuePercent: t.valuePercent,
      valueAmount: t.valueAmount,
      discountId: activeOrderDiscount.id,
      discountName: activeOrderDiscount.name,
      targetVariantIds: t.variantIds,
    }))
  }, [activeOrderDiscount, products, orderRewardItems, appliedCoupon])

  const availableCouponRewardItems = useMemo(() => {
    if (!appliedCoupon) return []
    const discount = appliedCoupon.discount || appliedCoupon
    if (discount.type !== 'order') return []
    const rewardProducts = discount.rewardProducts || []
    if (rewardProducts.length === 0) return []
    return rewardProducts.filter((r: any) => {
      const alreadyAdded = orderRewardItems.some((item: any) => item.id === r.id)
      return !alreadyAdded
    }).map((r: any) => ({
      ...r,
      discountBase: discount.discountBase || 'original',
      discountId: discount.id,
      discountName: discount.name,
      variant: r.variant || { id: r.defaultVariantId, price: r.price, priceSale: r.priceSale, stock: r.stock },
    }))
  }, [appliedCoupon, orderRewardItems])

  const hasFreeShipping = useMemo(() => {
    // A fresh server resolution is the only source; the local rule below applies only without one
    if (freshResolution) return freshResolution.freeShipping === true
    const freeShippingDiscount = automaticFreeShipping(activeOrderDiscount)
    const couponFreeShipping = appliedCoupon?.freeShipping

    if (!freeShippingDiscount && !couponFreeShipping) return false

    const discount = freeShippingDiscount || appliedCoupon?.discount || appliedCoupon
    const minOrderAmount = discount?.minOrderAmount || 0
    if (regularSubtotalAfterSale(products) < minOrderAmount) return false

    if (freeShippingDiscount) {
      // Exclude targets never qualify a line; a line one of them matches does not ship free
      const targetList = (freeShippingDiscount.targets || []).filter((t: any) => t.role !== 'exclude')
      const excludeList = (freeShippingDiscount.targets || []).filter((t: any) => t.role === 'exclude' && t.targetId)
      const isExcluded = (product: any) => excludeList.some((t: any) => {
        if (t.targetType === 'product') return t.targetId === product.id
        if (t.targetType === 'category') {
          const productData = product.product || product
          return productData.categoryId === t.targetId || productData.categories?.includes(t.targetId)
        }
        return false
      })
      if (targetList.length === 0) return !products.some(isExcluded)
      const allMatch = products.every((product: any) => {
        if (isExcluded(product)) return false
        return targetList.some((t: any) => {
          if (t.targetId === null) return true
          if (t.targetType === 'product' && t.targetId === product.id) {
            if (t.variantIds?.length > 0) return t.variantIds.includes(product.variant?.id)
            return true
          }
          if (t.targetType === 'category') {
            const productData = product.product || product
            return productData.categoryId === t.targetId || productData.categories?.includes(t.targetId)
          }
          return false
        })
      })
      return allMatch
    }

    return true
  }, [activeOrderDiscount, appliedCoupon, products, freshResolution])

  const freeShippingProgress = useMemo(() => {
    const freeShippingDiscount = automaticFreeShipping(activeOrderDiscount)
    const couponFreeShipping = appliedCoupon?.freeShipping
    if (!freeShippingDiscount && !couponFreeShipping) return null
    const discount = freeShippingDiscount || appliedCoupon?.discount || appliedCoupon
    const minOrderAmount = discount?.minOrderAmount || 0
    if (minOrderAmount <= 0) return null

    const targetList = (freeShippingDiscount?.targets || []).filter((t: any) => t.role !== 'exclude')
    const regularProducts = products.filter((p: any) => !p.isRewardItem)
    let targetSubtotal = 0

    const getProductEffectivePrice = (product: any) => {
      const variant = product.variant
      if (!variant?.price) return 0
      const productData = product.product || product
      const discounts = filterDiscounts(productData?.discounts || product.discounts, isLoggedIn, variant?.id)
      const effective = getEffectivePrice(variant, discounts, isLoggedIn)
      return effective ?? variant.price
    }

    if (targetList.length === 0 || targetList.every((t: any) => t.targetId === null)) {
      targetSubtotal = regularSubtotalAfterSale(products)
    } else {
      for (const product of regularProducts) {
        const matches = targetList.some((t: any) => {
          if (t.targetId === null) return true
          if (t.targetType === 'product' && t.targetId === product.id) {
            if (t.variantIds?.length > 0) return t.variantIds.includes(product.variant?.id)
            return true
          }
          if (t.targetType === 'category') {
            const pd = product.product || product
            return pd.categoryId === t.targetId || pd.categories?.includes(t.targetId)
          }
          return false
        })
        if (matches) {
          targetSubtotal += getProductEffectivePrice(product) * (product.quantity || 0)
        }
      }
      if (targetSubtotal === 0) return null
    }

    const percentage = Math.min(100, Math.round((targetSubtotal / minOrderAmount) * 100))
    const remaining = Math.max(0, minOrderAmount - targetSubtotal)
    return {
      percentage,
      remaining,
      minOrderAmount,
      currentAmount: targetSubtotal,
      reached: targetSubtotal >= minOrderAmount,
      discountName: discount?.name || 'Үнэгүй хүргэлт'
    }
  }, [activeOrderDiscount, appliedCoupon, products, isLoggedIn])

  useEffect(() => {
    if (!activeOrderDiscount || activeOrderDiscount.type !== 'order') return
    const minOrderAmount = activeOrderDiscount.minOrderAmount || 0
    const regularAmount = regularSubtotalAfterSale(products)
    if (regularAmount < minOrderAmount && orderRewardItems.length > 0) {
      setCart((prev: any) => ({ ...prev, products: (prev?.products || []).filter((p: any) => !(p.isRewardItem && p.discountType === 'order')) }))
      removeRewardItemsByType('order')
      toast.info('Захиалгын доод дүнд хүрээгүй тул бэлгийн бараа хасагдлаа')
    }
  }, [products, activeOrderDiscount, orderRewardItems])

  useEffect(() => {
    const couponDisc = appliedCoupon?.discount || appliedCoupon
    if (couponDisc?.type !== 'order') return
    const minOrderAmount = couponDisc.minOrderAmount || 0
    if (minOrderAmount <= 0) return
    const regularAmount = regularSubtotalAfterSale(products)
    if (regularAmount < minOrderAmount) {
      if (orderRewardItems.length > 0) {
        setCart((prev: any) => ({ ...prev, products: (prev?.products || []).filter((p: any) => !(p.isRewardItem && p.discountType === 'order')) }))
        removeRewardItemsByType('order')
      }
      setAppliedCoupon(null)
      try { sessionStorage.removeItem('appliedCouponCode') } catch (e) {}
      toast.info('Захиалгын доод дүнд хүрээгүй тул хөнгөлөлтийн код хасагдлаа')
    }
  }, [products, appliedCoupon, orderRewardItems])

  useEffect(() => {
    const couponDisc = appliedCoupon?.discount || appliedCoupon
    if (couponDisc?.type === 'buyXgetY') {
      const info = (buyXGetYInfo || []).find((i: any) => i.discount.id === couponDisc.id)
      if (!info || !info.qualifies) {
        setAppliedCoupon(null)
        try { sessionStorage.removeItem('appliedCouponCode') } catch (e) {}
      }
    }

    if (rewardItems.length === 0) return
    const trimUpdates: any[] = []

    const rewardsByDiscount = new Map<string, any[]>()
    for (const item of rewardItems) {
      const dId = item.discountId || ''
      if (!rewardsByDiscount.has(dId)) rewardsByDiscount.set(dId, [])
      rewardsByDiscount.get(dId)!.push(item)
    }

    for (const [discountId, items] of rewardsByDiscount) {
      const info = (buyXGetYInfo || []).find((i: any) => i.discount.id === discountId)
      if (!info || !info.qualifies) {
        for (const item of items) {
          trimUpdates.push({ id: item.id, variantId: item.variant?.id, quantity: 0, isRewardItem: true })
        }
        continue
      }
      const maxReward = info.maxRewardCount
      if (info.getYMatchMode !== 'all') {
        const totalRewardQty = items.reduce((t: number, r: any) => t + (r.quantity || 0), 0)
        const productCount = new Set(items.map((r: any) => r.id)).size
        if (totalRewardQty > maxReward || productCount > info.getYProductLimit) {
          let remaining = maxReward
          const allowedIds = new Set<string>()
          for (const item of items) {
            if (!allowedIds.has(item.id) && allowedIds.size >= info.getYProductLimit) {
              trimUpdates.push({ id: item.id, variantId: item.variant?.id, quantity: 0, isRewardItem: true })
              continue
            }
            allowedIds.add(item.id)
            if (remaining <= 0) {
              trimUpdates.push({ id: item.id, variantId: item.variant?.id, quantity: 0, isRewardItem: true })
            } else {
              const qty = Math.min(item.quantity || 0, remaining)
              if (qty !== item.quantity) {
                trimUpdates.push({ id: item.id, variantId: item.variant?.id, quantity: qty, isRewardItem: true, discountId: item.discountId, discountName: item.discountName, discountType: item.discountType, valueType: item.valueType, valuePercent: item.valuePercent, valueAmount: item.valueAmount, discountBase: item.discountBase, originalPrice: item.originalPrice })
              }
              remaining -= qty
            }
          }
        }
      } else {
        for (const item of items) {
          if ((item.quantity || 0) > maxReward) {
            trimUpdates.push({ id: item.id, variantId: item.variant?.id, quantity: maxReward, isRewardItem: true, discountId: item.discountId, discountName: item.discountName, discountType: item.discountType, valueType: item.valueType, valuePercent: item.valuePercent, valueAmount: item.valueAmount, discountBase: item.discountBase, originalPrice: item.originalPrice })
          }
        }
      }
    }

    if (trimUpdates.length > 0) {
      const vendorId = vendor?.id || vendor
      const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
      sendClientRequest({ url: '/cart', method: 'POST', auth: false, headers, body: { products: trimUpdates } }).then(() => fetchCart())
      if (trimUpdates.some((u: any) => u.quantity === 0)) {
        toast.info('Хөнгөлөлтийн нөхцөл хангагдаагүй тул бэлгийн бараа хасагдлаа')
      }
    }
  }, [buyXGetYInfo])

  const addRewardItem = useCallback(async (item: any, type: 'buyXgetY' | 'order' = 'buyXgetY', chosenVariantId?: string) => {
    const rewardVariants = item.variants || []
    if (!chosenVariantId && rewardVariants.length > 1) {
      setPendingReward({ item, type })
      return
    }
    setRewardLoading(true)
    try {
      const baseVariant = rewardVariants.find((v: any) => v.id === chosenVariantId) || rewardVariants[0] || item.variant || {}
      const variantId = baseVariant.id || item.defaultVariantId
      const stock = baseVariant.stock ?? item.stock ?? null
      const isTrackable = item.isTrackable !== false
      const existingReward = allCartProducts.find((p: any) => p.isRewardItem && p.id === item.id && p.variant?.id === variantId)
      const newQty = existingReward ? (existingReward.quantity || 0) + 1 : 1

      if (isTrackable && stock !== null && stock <= 0) {
        toast.error('Бэлгийн барааны үлдэгдэл дууссан байна')
        setRewardLoading(false)
        return
      }
      if (isTrackable && stock !== null && newQty > stock) {
        toast.error('Бэлгийн барааны үлдэгдэл хүрэлцэхгүй байна')
        setRewardLoading(false)
        return
      }

      const vendorId = vendor?.id || vendor
      const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
      await sendClientRequest({
        url: '/cart', method: 'POST', auth: false, headers,
        body: {
          products: [{
            id: item.id,
            variantId,
            quantity: newQty,
            isRewardItem: true,
            discountId: item.discountId,
            discountName: item.discountName,
            discountType: type,
            valueType: item.valueType,
            valuePercent: item.valuePercent,
            valueAmount: item.valueAmount,
            discountBase: item.discountBase || 'original',
            originalPrice: baseVariant.price || item.price,
          }]
        },
      })
      await fetchCart()
      toast.success('Бэлгийн бараа нэмэгдлээ')
    } finally {
      setRewardLoading(false)
    }
  }, [allCartProducts, vendor, sessionId, isGuest])

  const closeRewardPicker = useCallback(() => setPendingReward(null), [])

  // Closed without choosing: a picker the cart opened by itself is not offered again this session
  const dismissRewardPicker = useCallback(() => {
    if (pendingReward?.auto && pendingReward.item?.discountId) dismissReward(pendingReward.item.discountId)
    setPendingReward(null)
  }, [pendingReward])

  const removeRewardItem = useCallback(async (productId: string, variantId: string, _type: 'buyXgetY' | 'order' = 'buyXgetY') => {
    const removed = allCartProducts.find((p: any) => p.isRewardItem && p.id === productId && p.variant?.id === variantId)
    if (removed?.discountId) dismissReward(removed.discountId)
    setCart((prev: any) => {
      if (!prev?.products) return prev
      const updated = prev.products.filter((p: any) => !(p.isRewardItem && p.id === productId && p.variant?.id === variantId))
      return { ...prev, products: updated }
    })
    const vendorId = vendor?.id || vendor
    const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
    sendClientRequest({
      url: '/cart', method: 'POST', auth: false, headers,
      body: { products: [{ id: productId, variantId, quantity: 0, isRewardItem: true }] },
    })
    toast.success('Бэлгийн бараа хасагдлаа')
  }, [allCartProducts, vendor, sessionId, isGuest])

  const removeRewardItemsByType = useCallback(async (type: 'buyXgetY' | 'order') => {
    const items = allCartProducts.filter((p: any) => p.isRewardItem && p.discountType === type)
    if (items.length === 0) return
    const vendorId = vendor?.id || vendor
    const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
    await sendClientRequest({
      url: '/cart', method: 'POST', auth: false, headers,
      body: { products: items.map((r: any) => ({ id: r.id, variantId: r.variant?.id, quantity: 0, isRewardItem: true })) },
    })
    await fetchCart()
  }, [allCartProducts, vendor, sessionId, isGuest])

  const mergeCodeDiscounts = (validatedProducts: any[], originalProducts: any[]) => {
    return validatedProducts.map((vp: any) => {
      const original = originalProducts.find((op: any) => op.id === vp.id && op.variant?.id === vp.variant?.id)
      if (original?.discounts?.length) {
        const codeDiscounts = original.discounts.filter((d: any) => d.method === 'code')
        if (codeDiscounts.length) {
          const existingIds = new Set((vp.discounts || []).map((d: any) => d.id))
          return { ...vp, discounts: [...(vp.discounts || []), ...codeDiscounts.filter((d: any) => !existingIds.has(d.id))] }
        }
      }
      return vp
    })
  }

  const validateCart = async () => {
    if (!cart?.products?.length || (!vendor?.id && !vendor)) return
    setIsValidating(true)
    try {
      const vendorId = vendor?.id || vendor
      const { data: result, error } = await validateCartApi(vendorId, cart.products, cart?.id)
      if (error) {
        toast.error('Сагс шалгахад алдаа гарлаа')
        return
      }
      if (result.products && result.products.length > 0) {
        setCart((prev: any) => ({ ...prev, products: mergeCodeDiscounts(result.products, prev?.products || []) }))
      }
    } catch (error) {
      console.error('Cart validation error:', error)
      toast.error('Сагс шалгахад алдаа гарлаа')
    } finally {
      setIsValidating(false)
    }
  }

  const canCheckout = () => {
    if (!cart?.products?.length) return false
    const hasOutOfStockItems = cart.products.some((product: any) => product.quantity === 0 || product.isOutOfStock)
    if (hasOutOfStockItems) return false
    const hasValidProducts = cart.products.every((product: any) =>
      product.id &&
      product.variant?.id &&
      product.quantity > 0
    )
    if (!hasValidProducts) return false
    if (vendor?.orderMinAmount && (amount - sale) < vendor.orderMinAmount) return false
    return true
  }

  const fetchCart = async ({ newSessionId }: { newSessionId?: any } = {}) => {
    if (!cart) {
      setInitialLoading(true)
    } else {
      setLoading(true)
    }

    try {
      const vendorId = vendor?.id || vendor
      const headers: any = shopperHeaders({ vendorId, sessionId: newSessionId || sessionId, isGuest, accessToken })
      const { data: fetchedCart, error: cartError } = await sendClientRequest({ url: `/cart`, auth: false, headers: headers })

      if (cartError) {
        console.warn("Error fetching cart:", cartError)
        return
      }

      if (fetchedCart && fetchedCart.products && fetchedCart.products.length > 0) {
        const maxQuantity = vendor?.cartMaxQuantity || 999
        const adjustedProducts: any[] = []

        for (const product of fetchedCart.products) {
          if (product.quantity > maxQuantity) {
            try {
              await sendClientRequest({ url: '/cart', method: 'POST', auth: false, headers: headers, body: { products: [{ id: product.id, quantity: maxQuantity, variantId: product.variant.id }] } })
              adjustedProducts.push({ ...product, quantity: maxQuantity })
            } catch (error) {
              console.error('Error adjusting product quantity:', error)
              adjustedProducts.push(product)
            }
          } else {
            adjustedProducts.push(product)
          }
        }

        const { data: validateResult } = await validateCartApi(vendorId, adjustedProducts, fetchedCart?.id)
        if (validateResult?.products) {
          setCart({ ...fetchedCart, products: mergeCodeDiscounts(validateResult.products, adjustedProducts) })
        } else {
          setCart({ ...fetchedCart, products: adjustedProducts })
        }
      } else {
        setCart(null)
      }
    } catch (err) {
      console.error("Error fetching cart:", err)
    } finally {
      setInitialLoading(false)
      setLoading(false)
    }
  }

  const addToCart = async ({ productId, variantId, quantity }: { productId: string, variantId: string, quantity: number }) => {
    setLoading(true)

    const vendorId = vendor?.id || vendor
    const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })

    const existingProduct = cart?.products?.find((p: any) => p.id === productId && p.variant?.id === variantId && !p.isRewardItem)
    const currentQuantity = existingProduct ? (existingProduct.quantity || 0) : 0
    let finalQuantity = currentQuantity + quantity
    const maxQuantity = vendor?.cartMaxQuantity || 999
    let wasReduced = false

    if (finalQuantity > maxQuantity) {
      finalQuantity = maxQuantity
      wasReduced = true
      toast.warning(`Нэг бүтээгдэхүүнээс хамгийн ихдээ ${maxQuantity} ширхэгийг захиалах боломжтой`)
    }

    const actuallyAdded = finalQuantity > currentQuantity
    const isNewProduct = !existingProduct

    const res = await sendClientRequest({ url: '/cart', method: 'POST', auth: false, headers: headers, body: { products: [{ id: productId, quantity: finalQuantity, variantId: variantId }] } })

    if (!res?.status || res.status !== 200) {
      const errorMessage = res?.error || 'Сагсанд нэмэхэд алдаа гарлаа'
      toast.error(errorMessage)
      setLoading(false)
      return
    }

    await fetchCart()
    if (isNewProduct) {
      toast.success('Сагсанд нэмэгдлээ')
    } else if (actuallyAdded) {
      toast.success('Сагсанд дахин нэмэгдлээ')
    } else if (!wasReduced) {
      toast.info('Бүтээгдэхүүн аль хэдийн сагсанд байна')
    }
    if (actuallyAdded) {
      const added = res?.data?.products?.find((p: any) => p.id === productId && p.variant?.id === variantId)
      if (added) trackAddToCart({ product: added, variant: added.variant, quantity })
    }
    setLoading(false)
  }

  const updateQuantityByDelta = async ({ productId, variantId, delta }: { productId: string, variantId: string, delta: number }) => {
    if (!vendor || !cart?.products) return
    const product = cart.products.find((p: any) => p.id === productId && p.variant?.id === variantId)
    if (!product) return
    const currentQty = product.quantity || 1
    let newQty = currentQty + delta
    const maxQuantity = vendor?.cartMaxQuantity || 999
    if (newQty <= 0) {
      return removeFromCart({ productId, variantId })
    } else {
      if (newQty > maxQuantity) {
        newQty = maxQuantity
        toast.warning(`Нэг бүтээгдэхүүнээс хамгийн ихдээ ${maxQuantity} ширхэгийг захиалах боломжтой`)
      }
      return updateQuantity({ productId, variantId, quantity: newQty })
    }
  }

  const addOne = async ({ productId, variantId }: { productId: string, variantId: string }) => {
    if (!cart?.products) return
    const product = cart.products.find((p: any) => p.id === productId && p.variant?.id === variantId)
    if (!product) return
    const stock = product.variant?.stock ?? 0
    const currentQty = product.quantity || 1
    const maxQuantity = vendor?.cartMaxQuantity || 999
    if (product.isTrackable && currentQty >= stock) {
      toast.error('Бүтээгдэхүүний үлдэгдэл дууссан байна')
      return
    }
    if (currentQty >= maxQuantity) {
      toast.warning(`Нэг бүтээгдэхүүнээс хамгийн ихдээ ${maxQuantity} ширхэгийг захиалах боломжтой`)
      return
    }
    const result = await updateQuantityByDelta({ productId, variantId, delta: 1 })
    return result
  }

  const removeOne = async ({ productId, variantId }: { productId: string, variantId: string }) => {
    const result = await updateQuantityByDelta({ productId, variantId, delta: -1 })
    return result
  }

  const updateQuantity = async ({ productId, variantId, quantity }: { productId: string, variantId: string, quantity: number }) => {
    if (!vendor) return
    const maxQuantity = vendor?.cartMaxQuantity || 999
    if (quantity > maxQuantity) {
      toast.warning(`Нэг бүтээгдэхүүнээс хамгийн ихдээ ${maxQuantity} ширхэгийг захиалах боломжтой`)
      quantity = maxQuantity
    }
    const vendorId = vendor?.id || vendor
    const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
    setLoading(true)
    try {
      const res = await sendClientRequest({ url: '/cart', method: 'POST', auth: false, headers: headers, body: { products: [{ id: productId, quantity, variantId: variantId }] } })
      if (!res?.status || res.status !== 200) {
        console.error('Error updating quantity:', res)
        toast.error('Барааны тоо ширхэгийг өөрчлөхөд алдаа гарлаа')
        await fetchCart()
        return
      }

      if (cart?.products) {
        const updatedProducts = cart.products.map((p: any) => {
          if (p.id === productId && p.variant?.id === variantId && !p.isRewardItem) {
            return { ...p, quantity }
          }
          return p
        })
        const remainingRegular = updatedProducts.filter((p: any) => !p.isRewardItem)
        const remainingRewards = updatedProducts.filter((p: any) => p.isRewardItem && p.discountType === 'order')
        const couponDisc = appliedCoupon?.discount || appliedCoupon
        const orderDisc = couponDisc?.type === 'order' ? couponDisc : activeOrderDiscount?.type === 'order' ? activeOrderDiscount : null
        const minOrderAmount = orderDisc?.minOrderAmount || 0
        const regularAmount = regularSubtotalAfterSale(remainingRegular)

        if (remainingRewards.length > 0 && regularAmount < minOrderAmount) {
          const cleanProducts = updatedProducts.filter((p: any) => !(p.isRewardItem && p.discountType === 'order'))
          setCart({ ...cart, products: cleanProducts })
          if (couponDisc?.type === 'order') {
            setAppliedCoupon(null)
            try { sessionStorage.removeItem('appliedCouponCode') } catch (e) {}
          }
          const rewardRemovals = remainingRewards.map((r: any) => ({ id: r.id, variantId: r.variant?.id, quantity: 0, isRewardItem: true }))
          sendClientRequest({ url: '/cart', method: 'POST', auth: false, headers, body: { products: rewardRemovals } })
          toast.info('Захиалгын доод дүнд хүрээгүй тул бэлгийн бараа хасагдлаа')
        } else {
          setCart({ ...cart, products: updatedProducts })
        }
      }
    } catch (error) {
      console.error('Error updating cart:', error)
      await fetchCart()
    } finally {
      setLoading(false)
    }
  }

  const removeFromCart = async ({ productId, variantId }: { productId: string, variantId: string }) => {
    if (!vendor) return
    const vendorId = vendor?.id || vendor
    const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
    setLoading(true)

    try {
      const res = await sendClientRequest({ url: '/cart', method: 'POST', auth: false, headers: headers, body: { products: [{ id: productId, quantity: 0, variantId: variantId }] } })

      if (!res?.status || res.status !== 200) {
        console.error('Error removing from cart:', res)
        toast.error('Сагснаас хасахад алдаа гарлаа')
        await fetchCart()
        return
      }

      if (cart?.products) {
        let updatedProducts = cart.products.filter((p: any) => !(p.id === productId && p.variant?.id === variantId && !p.isRewardItem))
        const remainingRegular = updatedProducts.filter((p: any) => !p.isRewardItem)
        const allRemovals: any[] = []

        const buyXgetYRewards = updatedProducts.filter((p: any) => p.isRewardItem && p.discountType === 'buyXgetY')
        if (buyXgetYRewards.length > 0) {
          const rewardsByDiscount = new Map<string, any[]>()
          for (const r of buyXgetYRewards) {
            const dId = r.discountId || ''
            if (!rewardsByDiscount.has(dId)) rewardsByDiscount.set(dId, [])
            rewardsByDiscount.get(dId)!.push(r)
          }
          for (const [discountId, items] of rewardsByDiscount) {
            const info = (buyXGetYInfo || []).find((i: any) => i.discount.id === discountId)
            const hasTrigger = remainingRegular.some((p: any) => {
              const rawDiscounts = p.discounts || (p.product || p)?.discounts
              const bxgy = getBuyXGetYDiscount(rawDiscounts)
              return bxgy?.id === discountId && bxgy.role === 'trigger'
            })
            if (!hasTrigger || !info?.qualifies) {
              for (const item of items) {
                allRemovals.push({ id: item.id, variantId: item.variant?.id, quantity: 0, isRewardItem: true })
              }
              updatedProducts = updatedProducts.filter((p: any) => !(p.isRewardItem && p.discountId === discountId && p.discountType === 'buyXgetY'))
            }
          }
        }

        const orderRewards = updatedProducts.filter((p: any) => p.isRewardItem && p.discountType === 'order')
        const couponDisc = appliedCoupon?.discount || appliedCoupon
        const orderDisc = couponDisc?.type === 'order' ? couponDisc : activeOrderDiscount?.type === 'order' ? activeOrderDiscount : null
        const minOrderAmount = orderDisc?.minOrderAmount || 0
        const regularAmount = regularSubtotalAfterSale(remainingRegular)

        if (orderRewards.length > 0 && regularAmount < minOrderAmount) {
          updatedProducts = updatedProducts.filter((p: any) => !(p.isRewardItem && p.discountType === 'order'))
          if (couponDisc?.type === 'order') {
            setAppliedCoupon(null)
            try { sessionStorage.removeItem('appliedCouponCode') } catch (e) {}
          }
          for (const r of orderRewards) {
            allRemovals.push({ id: r.id, variantId: r.variant?.id, quantity: 0, isRewardItem: true })
          }
        }

        setCart({ ...cart, products: updatedProducts })
        if (allRemovals.length > 0) {
          sendClientRequest({ url: '/cart', method: 'POST', auth: false, headers, body: { products: allRemovals } })
          toast.info('Хөнгөлөлтийн нөхцөл хангагдаагүй тул бэлгийн бараа хасагдлаа')
        }
      }
      toast.success('Бүтээгдэхүүн амжилттай сагснаас хасагдлаа')
    } catch (err) {
      console.error('Error removing from cart:', err)
      toast.error('Сагснаас хасахад алдаа гарлаа')
      await fetchCart()
    } finally {
      setLoading(false)
    }
  }

  const checkout = async ({ fullName, email, phone, additionalPhone, cartId, selectedDelivery, selectedPayment, addressObj, branchId, products: checkoutProducts, giftCardId, giftCardAmount, giftCardCode, loyaltyVoucherId, loyaltyVoucherCode, storepayPhone, lendPhone, productCustomFields, orderCustomFields }: { fullName: string, email?: string, phone: string, additionalPhone?: string, cartId: string, selectedDelivery: string, selectedPayment: string, addressObj: any, branchId: any, products: any[], giftCardId?: string, giftCardAmount?: number, giftCardCode?: string, loyaltyVoucherId?: string, loyaltyVoucherCode?: string, storepayPhone?: string, lendPhone?: string, productCustomFields?: { [key: string]: any[] }, orderCustomFields?: any[] }) => {
    if (!vendor) return
    if (isGuest && !vendor.canGuestOrder && isCustomerAuthActive(vendor)) {
      toast.error('Зөвхөн бүртгэлтэй хэрэглэгч захиалга хийх боломжтой')
      return { requiresLogin: true }
    }
    const vendorId = vendor?.id || vendor
    const finalCartId = cartId || cart?.id
    const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })

    const checkoutTotal = (checkoutProducts || []).reduce((sum: number, p: any) => {
      const price = getEffectivePrice(p.variant, p.discounts, isLoggedIn) || 0
      return sum + price * (p.quantity || 1)
    }, 0)
    trackInitiateCheckout({ products: checkoutProducts, totalValue: checkoutTotal })

    setLoading(true)
    try {
      const enhancedProducts: any[] = []

      for (const product of checkoutProducts) {
        if (product.isRewardItem) {
          enhancedProducts.push({
            productId: product.id,
            variantId: product.variant.id,
            quantity: product.quantity || 1,
            isRewardItem: true,
            discountId: product.discountId,
            discountName: product.discountName,
            discountType: product.discountType,
            valueType: product.valueType,
            valuePercent: product.valuePercent,
            valueAmount: product.valueAmount,
            originalPrice: product.originalPrice || product.variant?.price,
          })
        } else {
          const baseProduct: any = {
            productId: product.id,
            variantId: product.variant.id,
            quantity: product.quantity || 1,
            isRegularItem: true,
          }
          if (productCustomFields && productCustomFields[product.id]) {
            baseProduct.customFields = productCustomFields[product.id]
          }
          enhancedProducts.push(baseProduct)
        }
      }

      const discount = appliedCoupon?.discount || appliedCoupon
      const discountCode = discount?.method === 'code' ? discount.code : undefined
      const discountId = activeOrderDiscount?.id || buyXGetYInfo?.[0]?.discount?.id || undefined

      const orderData: any = {
        fullName,
        email,
        phone,
        additionalPhone,
        cartId: finalCartId,
        sessionId: sessionId,
        type: selectedDelivery,
        paymentMethod: selectedPayment,
        address: addressObj,
        branchId,
        products: enhancedProducts,
        giftCardId: giftCardId || null,
        giftCardAmount: giftCardAmount || 0,
        giftCardCode: giftCardCode || null,
        loyaltyVoucherId: loyaltyVoucherId || null,
        loyaltyVoucherCode: loyaltyVoucherCode || null,
        storepayPhone: selectedPayment === 'storepay' ? storepayPhone : null,
        lendPhone: selectedPayment === 'lend' ? lendPhone : null,
        productCustomFields,
        orderCustomFields,
        metaTracking: getMetaTrackingContext(),
      }

      if (discountCode) orderData.discountCode = discountCode
      if (discountId) orderData.discountId = discountId

      const { data, error } = await sendClientRequest({ method: 'POST', auth: false, url: '/order', body: orderData, headers: headers })
      setLoading(false)
      if (error || !data) return { error, success: false }
      if (appliedGiftCard) removeGiftCard()
      if (appliedLoyaltyVoucher) removeLoyaltyVoucher()
      if (appliedCoupon) {
        setAppliedCoupon(null)
        try { sessionStorage.removeItem('appliedCouponCode') } catch (e) {}
      }
      toast.success('Захиалга амжилттай үүсгэлээ')
      trackAddPaymentInfo({ products: checkoutProducts, totalValue: checkoutTotal })
      setCart({ ...cart, products: [] })
      await fetchCart()
      return data
    } catch (err) {
      console.error('Error during checkout:', err)
      toast.error('Захиалга үүсгэхэд алдаа гарлаа')
    } finally {
      setLoading(false)
    }
  }

  const clearCart = async () => {
    if (!cart?.products?.length) {
      toast.info('Сагс аль хэдийн хоосон байна')
      return
    }
    setLoading(true)
    try {
      const vendorId = vendor?.id || vendor
      const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
      const res = await sendClientRequest({ url: `/cart/${cart.id}`, method: 'DELETE', auth: false, headers })
      if (res?.status === 200) {
        setCart(null)
        toast.success('Сагс амжилттай хоослогдлоо')
      } else {
        throw new Error(res?.error || 'Сагс хоослоход алдаа гарлаа')
      }
    } catch (error) {
      console.error('Error clearing cart:', error)
      toast.error('Сагс хоослохад алдаа гарлаа')
      await fetchCart()
    } finally {
      setLoading(false)
    }
  }

  const calculateCartCount = () => {
    if (!cart?.products?.length) return 0
    return cart.products.filter((p: any) => !p.isRewardItem).length
  }

  // Tier-iin hongololtiin urdchilsan dun — server yag createOrder-iin
  // logikoor bodno, ende zovhon haruulna. Guest-ed baihgui.
  const loyaltyPreviewKey = useMemo(() => JSON.stringify(products.map((p: any) => [p.id, p.variant?.id, p.quantity])), [products])
  useEffect(() => {
    if (!isLoggedIn || !isLoyaltyActive(vendor) || !products.length) {
      setLoyaltyPreview(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      const { data } = await sendClientRequest({
        url: '/loyalty/checkout/preview', method: 'POST', auth: true, session: (session as any) || undefined,
        body: { products: products.map((p: any) => ({ id: p.id, variantId: p.variant?.id, quantity: p.quantity || 1 })) },
        headers: { Vendor: vendor?.id || vendor },
      })
      if (!cancelled) setLoyaltyPreview(data ? { ...data, forKey: loyaltyPreviewKey } : null)
    }, 400)
    return () => { cancelled = true; clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loyaltyPreviewKey, isLoggedIn, session, vendor])

  // Tier-iin medeelel (benefits + targets) — product card / detail deer
  // huviin hongololtiig haruulahad heregtei. Session-d neg l udaa tatna.
  useEffect(() => {
    if (!isLoggedIn || !isLoyaltyActive(vendor)) {
      setLoyaltySummary(null)
      loyaltySeededRef.current = false
      return
    }
    if (loyaltySeededRef.current) {
      loyaltySeededRef.current = false
      return
    }
    let cancelled = false
    sendClientRequest({ url: '/loyalty/me', auth: true, session: (session as any) || undefined, headers: { Vendor: vendor?.id || vendor } }).then((res: any) => {
      if (cancelled || !res?.data) return
      setLoyaltySummary(res.data)
    }).catch(() => { })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, vendor?.id])

  // Server preview 400ms debounce + round-trip hurtel gross dun haragdahgui
  // bailgahiin tuld client mirror-oor shuud tootsson dun fallback bolno —
  // preview irmegts server-iin toog l ashiglana.
  const loyaltyClientEstimate = useMemo(() => {
    if (!loyaltySummary?.currentTier?.benefits?.length) return { amount: 0, saleOffset: 0 }
    return (cart?.products || []).reduce((acc: { amount: number, saleOffset: number }, line: any) => {
      if (line.isRewardItem) return acc
      const productData = line.product || line
      const lineDiscounts = filterDiscounts(productData?.discounts, isLoggedIn, line.variant?.id)
      const deal = getLoyaltyProductDiscount({ product: productData, variant: line.variant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: lineDiscounts?.length > 0 && isDiscountBetter(line.variant, lineDiscounts, isLoggedIn) })
      if (!deal) return acc
      const qty = line.quantity || 1
      return { amount: acc.amount + deal.unitSaving * qty, saleOffset: acc.saleOffset + (deal.saleOffset || 0) * qty }
    }, { amount: 0, saleOffset: 0 })
  }, [cart?.products, loyaltySummary, loyaltyCategoryParents, isLoggedIn])

  // Too hemjee oorchlogdohod huuchin preview-g ashiglaval shine dun - huuchin
  // hongololt geed buruu haragdana — preview ali sagsand bodogdsniig temdegleed,
  // sags oorchlogdson uyed serverees shine dun ireh hurtel client mirror-t naidna.
  const loyaltyPreviewFresh = loyaltyPreview && loyaltyPreview.forKey === loyaltyPreviewKey
  const loyaltyDiscountAmount = loyaltyPreviewFresh ? (loyaltyPreview.discountAmount || 0) : loyaltyClientEstimate.amount
  // Sale-iig orlson hesgiig nemj haruulna: hereglegchid "undsen uneess -N%"
  // gej haragdana, mongon dun oorchlogdohgui (sale mor ni mun ene helberees hasagdana).
  const loyaltySaleOffsetAmount = loyaltyPreviewFresh ? (loyaltyPreview.saleOffsetAmount || 0) : loyaltyClientEstimate.saleOffset
  const loyaltyDisplayDiscountAmount = loyaltyDiscountAmount + loyaltySaleOffsetAmount

  const loyaltyVoucherDiscount = useMemo(() => {
    if (!appliedLoyaltyVoucher) return 0
    return Number(appliedLoyaltyVoucher.remainingAmount ?? appliedLoyaltyVoucher.amount) || 0
  }, [appliedLoyaltyVoucher])

  const applyLoyaltyVoucher = (voucher: any) => {
    if (!voucher?.id || voucher.status !== 'active') return false
    setAppliedLoyaltyVoucher(voucher)
    return true
  }

  const removeLoyaltyVoucher = () => {
    setAppliedLoyaltyVoucher(null)
  }

  const validateGiftCard = async (code: string) => {
    try {
      const { data, error } = await sendClientRequest({ url: `/giftCard/validate/${code}`, auth: false, headers: { Vendor: vendor.id || vendor } })
      if (error) return { valid: false, error }
      return { valid: true, data }
    } catch (error) {
      return { valid: false, error: 'Алдаа гарлаа' }
    }
  }

  const applyGiftCard = async (code: string) => {
    try {
      const validation = await validateGiftCard(code)
      if (!validation.valid) return false

      const giftCard = validation.data.giftCard
      setAppliedGiftCard(giftCard)

      const giftCardAmount = Number(giftCard.amount) || Number(giftCard.giftCard?.amount) || 0
      setGiftCardDiscount(giftCardAmount)

      return true
    } catch (error) {
      return false
    }
  }

  const removeGiftCard = () => {
    setAppliedGiftCard(null)
    setGiftCardDiscount(0)
  }

  const applyCoupon = async (code: string) => {
    try {
      const vendorId = vendor?.id || vendor
      const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })

      // No cartSubtotal: the server measures minOrderAmount on the regular lines at their sale price, as checkout does
      const result = await sendClientRequest({
        url: '/discount/validate',
        method: 'POST',
        auth: false,
        headers,
        body: { code, products: previewProducts },
      })

      if (result?.error || !result?.data) {
        return { success: false, error: result?.error, condition: result?.condition || null }
      }
      // Keeps beatenBy, discountAmount, applied and lines with the coupon, stamped with the cart they were resolved for
      storeCouponResult(result.data, resolutionKeyFor(result.data.discount?.code || code))
      try { sessionStorage.setItem('appliedCouponCode', code) } catch (e) {}
      return { success: true }
    } catch (error) {
      return { success: false }
    }
  }

  const removeCoupon = () => {
    const couponDisc = appliedCoupon?.discount || appliedCoupon
    setAppliedCoupon(null)
    try { sessionStorage.removeItem('appliedCouponCode') } catch (e) {}
    if (couponDisc?.type === 'buyXgetY') {
      removeRewardItemsByType('buyXgetY')
    } else if (couponDisc?.type === 'order') {
      removeRewardItemsByType('order')
    }
  }

  const getRewardPrice = (reward: any) => {
    const rewardPrice = reward.price || 0
    return Math.max(0, rewardPrice - unitSaving(rewardPrice, reward))
  }

  const isRewardOutOfStock = (reward: any) => {
    if (reward.isTrackable === false) return false
    if (reward.variants?.length > 0) return reward.variants.every((v: any) => (v.stock ?? 0) <= 0)
    const rewardStock = reward.stock ?? reward.variant?.stock ?? null
    return rewardStock !== null && rewardStock <= 0
  }

  const getBuyXGetYRewardState = (info: any, reward: any) => {
    const discountRewards = rewardItems.filter((r: any) => r.discountId === info.discount.id)
    const totalRewardQty = discountRewards.reduce((t: number, r: any) => t + (r.quantity || 0), 0)
    const selectedRewardId = info.getYMatchMode !== 'all' && discountRewards.length > 0 ? discountRewards[0].id : null
    const thisRewardQty = discountRewards.filter((r: any) => r.id === reward.id).reduce((t: number, r: any) => t + (r.quantity || 0), 0)
    const remaining = info.getYMatchMode === 'all'
      ? info.maxRewardCount - thisRewardQty
      : info.maxRewardCount - totalRewardQty
    const totalRemaining = info.getYMatchMode === 'all'
      ? (info.maxRewardCount * (info.rewardProducts?.length || 0)) - totalRewardQty
      : info.maxRewardCount - totalRewardQty
    const selectedProductIds = new Set(discountRewards.map((r: any) => r.id))
    const isLockedByOther = !selectedProductIds.has(reward.id) && selectedProductIds.size >= info.getYProductLimit
    const isOutOfStock = isRewardOutOfStock(reward)
    const canAdd = !isLockedByOther && !isOutOfStock && remaining > 0
    const rewardPrice = getRewardPrice(reward)
    return { canAdd, isLockedByOther, isOutOfStock, remaining, totalRemaining, thisRewardQty, totalRewardQty, rewardPrice, selectedRewardId }
  }

  // Lowest regular price of a gift, over its variants when it has them
  const rewardBasePrice = (reward: any) => {
    const prices = (reward.variants || []).map((v: any) => v.price).filter((p: any) => typeof p === 'number')
    return prices.length > 0 ? Math.min(...prices) : (reward.variant?.price ?? reward.price ?? 0)
  }

  // The free gift the cart adds by itself: the one this discount already has in the cart, else the cheapest in stock
  const pickFreeReward = (rewards: any[], inCartId?: string) => {
    const free = rewards.filter((r: any) => r.valueType === 'free' && !isRewardOutOfStock(r))
    if (inCartId) return free.find((r: any) => r.id === inCartId) ?? null
    return free.reduce((best: any, r: any) => (!best || rewardBasePrice(r) < rewardBasePrice(best) ? r : best), null)
  }

  // Gift auto-add. A discount whose vendor switched `autoAddReward` off is left to the shopper's Add button.
  useEffect(() => {
    if (!cart || initialLoading || loading || rewardLoading) return
    const dismissed = getDismissedRewards()
    const candidates: { key: string, item: any }[] = []
    const pickers: { key: string, item: any, type: 'buyXgetY' | 'order' }[] = []

    const addCandidate = (reward: any, type: 'buyXgetY' | 'order', quantity: number, currentQty = 0, variantIds?: string[]) => {
      if (reward.valueType !== 'free' || dismissed.includes(reward.discountId)) return
      const variants = (reward.variants || []).filter((v: any) => !variantIds?.length || variantIds.includes(v.id))
      if (variants.length > 1) {
        // Several variants: the shopper picks one, once, before any of this gift is in the cart
        if (currentQty === 0) pickers.push({ key: `${reward.discountId}:${reward.id}`, item: { ...reward, variants }, type })
        return
      }
      const variant = variants[0] || reward.variant || { id: reward.defaultVariantId, price: reward.price, stock: reward.stock }
      const stock = reward.isTrackable !== false ? (variant.stock ?? reward.stock ?? null) : null
      const qty = stock !== null ? Math.min(quantity, stock) : quantity
      if (qty <= currentQty) return
      candidates.push({
        key: `${reward.discountId}:${reward.id}:${qty}`,
        item: {
          id: reward.id, variantId: variant.id || reward.defaultVariantId, quantity: qty, isRewardItem: true,
          discountId: reward.discountId, discountName: reward.discountName, discountType: type,
          valueType: reward.valueType, valuePercent: reward.valuePercent, valueAmount: reward.valueAmount,
          discountBase: reward.discountBase || 'original', originalPrice: variant.price || reward.price,
        },
      })
    }

    for (const info of buyXGetYInfo || []) {
      if (!info.qualifies || info.discount?.autoAddReward === false || !info.rewardProducts?.length) continue
      const inCart = rewardItems.find((r: any) => r.discountId === info.discount.id)
      const reward = pickFreeReward(info.rewardProducts, inCart?.id)
      if (!reward) continue
      const { canAdd, remaining, thisRewardQty } = getBuyXGetYRewardState(info, reward)
      if (!canAdd) continue
      addCandidate({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase }, 'buyXgetY', thisRewardQty + remaining, thisRewardQty)
    }

    if (orderRewardItems.length === 0 && activeOrderDiscount?.autoAddReward !== false) {
      const reward = pickFreeReward(availableOrderRewardItems)
      if (reward) addCandidate(reward, 'order', 1, 0, reward.targetVariantIds)
    }

    const couponDisc = appliedCoupon?.discount || appliedCoupon
    if (orderRewardItems.length === 0 && couponDisc?.autoAddReward !== false) {
      const reward = pickFreeReward(availableCouponRewardItems)
      const regularAmount = regularSubtotalAfterSale(products)
      if (reward && regularAmount >= (couponDisc?.minOrderAmount || 0)) addCandidate(reward, 'order', 1)
    }

    const picker = pickers.find((p) => !autoPickerKeysRef.current.has(p.key))
    if (picker && !pendingReward) {
      autoPickerKeysRef.current.add(picker.key)
      setPendingReward({ item: picker.item, type: picker.type, auto: true })
    }

    const fresh = candidates.filter((c) => !autoRewardKeysRef.current.has(c.key))
    autoRewardKeysRef.current = new Set(candidates.map((c) => c.key))
    if (fresh.length === 0) return

    const vendorId = vendor?.id || vendor
    const headers: any = shopperHeaders({ vendorId, sessionId, isGuest, accessToken })
    sendClientRequest({ url: '/cart', method: 'POST', auth: false, headers, body: { products: fresh.map((c) => c.item) } }).then(async ({ error }: any) => {
      if (error) return
      await fetchCart()
      toast.success('Бэлгийн бараа сагсанд нэмэгдлээ')
    })
  }, [buyXGetYInfo, availableOrderRewardItems, availableCouponRewardItems, loading, initialLoading, rewardLoading, pendingReward])

  // With a server resolution, the same pre-coupon, pre-order-discount subtotal useOrderTotal starts from
  const cartTotal = useMemo(() => (serverFigures ? amount - sale : getCartTotal(cart?.products || [], isLoggedIn)), [serverFigures, amount, sale, cart?.products, isLoggedIn])

  return (
    <CartContext.Provider value={{
      cart, vendor, initialLoading, loading, fetchCart, cartCount: calculateCartCount(), addToCart, addOne, removeOne, updateQuantity, removeFromCart, checkout, clearCart,
      validateGiftCard, applyGiftCard, appliedGiftCard, giftCardDiscount, removeGiftCard,
      applyLoyaltyVoucher, appliedLoyaltyVoucher, loyaltyVoucherDiscount, removeLoyaltyVoucher, loyaltyPreview, loyaltySummary, loyaltyCategoryParents, loyaltyDiscountAmount, loyaltySaleOffsetAmount, loyaltyDisplayDiscountAmount,
      applyCoupon, appliedCoupon, couponDiscount, removeCoupon,
      canGuestOrder: vendor?.canGuestOrder || !isCustomerAuthActive(vendor), validateCart, isValidating, canCheckout, isGuest,
      activeOrderDiscount, orderDiscountAmount, buyXGetYInfo, hasFreeShipping, freeShippingProgress,
      rewardItems, orderRewardItems, addRewardItem, removeRewardItem, availableOrderRewardItems, availableCouponRewardItems, rewardLoading, pendingReward, closeRewardPicker, dismissRewardPicker,
      getRewardPrice, isRewardOutOfStock, getBuyXGetYRewardState,
      amount, sale, salePriceSavings, discountSavings, discountBreakdown, allProducts, cartTotal, serverResolution: freshResolution,
    }}>
      {children}
    </CartContext.Provider>
  )
}

export default CartProvider
